import { useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';

const KEY = 'atlas-control.theme';
const listeners = new Set<() => void>();
const media = () => window.matchMedia('(prefers-color-scheme: dark)');

function read(): ThemePreference {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function resolvedTheme(pref: ThemePreference = read()): 'light' | 'dark' {
  return pref === 'system' ? (media().matches ? 'dark' : 'light') : pref;
}

/** Applies the theme class to <html>. Called at start-up and on every change. */
export function applyTheme(): void {
  document.documentElement.classList.toggle('dark', resolvedTheme() === 'dark');
}

export function setTheme(pref: ThemePreference): void {
  try {
    if (pref === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, pref);
  } catch {
    // Storage unavailable (private mode): the choice lasts for this page only.
  }
  applyTheme();
  listeners.forEach((l) => l());
}

export function initTheme(): void {
  applyTheme();
  media().addEventListener('change', () => {
    applyTheme();
    listeners.forEach((l) => l());
  });
}

export function useTheme() {
  const theme = useSyncExternalStore((l) => {
    listeners.add(l);
    return () => listeners.delete(l);
  }, read);
  return { theme, resolved: resolvedTheme(theme), setTheme };
}
